<template>
  <div
    :id="`${source}_task_message`"
    class="flex flex-col gap-2 py-2 px-4 task_message_container"
  >
    <span class="text-[11px] font-semibold secondary_color">{{ sentLabel }}</span>

    <div class="flex flex-row gap-2">
      <ProfileIcon :data="{}" :custom-avatar="computedProfileIcon" />

      <div class="flex flex-col gap-1 min-w-0 w-full">
        <div id="name" class="flex flex-row items-center gap-2">
          <span class="text-sm font-semibold primary_color">SuperAGI</span>
          <span class="text-[11px] secondary_color">{{
            formatTimestamp(
              timestamp,
              "timestamp",
              formatTypes.centered,
            )
          }}</span>
        </div>

        <span id="action" class="text-sm font-semibold primary_color">
          New task created ✅
        </span>

        <div
          :class="['grid grid-cols-12 rounded-lg bg_color border_8 transition-opacity', isExternalMember ? '' : 'cursor-pointer hover:opacity-80']"
          @click="navigateToTask"
        >
          <div
            v-for="(field, index) in taskFields"
            :key="`${field?.id}_task_field_${index}`"
            :class="[
              `col-span-${field.cols} p-3 text-sm primary_color`,
              index < taskFields.length - 1 ? 'border_right' : '',
            ]"
          >
            <div v-if="field.type === 'assignees'" class="flex -space-x-1">
              <ProfileIcon
                v-for="assignee in field.value"
                :key="assignee.id"
                :data="assignee"
                :size="20"
                :font="8"
                class="border border-[var(--background-color)] rounded-full"
              />
              <span v-if="!field.value?.length" class="text-xs secondary_color">—</span>
            </div>
            <template v-else>
              <a-tooltip v-if="isTruncated[index]" :title="field.displayValue || field.value">
                <span
                  :ref="(el) => setFieldRef(el, index)"
                  class="block truncate"
                >
                  {{ field.displayValue || field.value }}
                </span>
              </a-tooltip>
              <span
                v-else
                :ref="(el) => setFieldRef(el, index)"
                class="block truncate"
              >
                {{ field.displayValue || field.value }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { formatTypes } from "@/src/constants/dateTimeConstants.js";
import { chatTypes } from "@/src/constants/chatConstants.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";

const store = useStore();
const router = useRouter();

const props = defineProps({
  source: { type: String, required: true },
  task: { type: Object, default: null },
  message: { type: Object, default: null },
  chat: { type: Object, default: null },
});

const fieldRefs = ref([]);
const isTruncated = ref({});

const sentLabel = computed(() => {
  const chat = props.chat || store.getters["chats/activeChat"];
  if (!chat) return "Sent to group";
  const chatType = chat.type;
  const isDM = chat.is_dm === true || chatType === chatTypes.DM || chatType === chatTypes.SELF_DM;
  const isGroupDM = chatType === chatTypes.GROUP_DM;
  if (isDM && !isGroupDM) return "Sent as direct message";
  return "Sent to group";
});

// Resolve task data from either the direct task prop or the message's embedded task object
const resolvedTask = computed(() => {
  return props.task || props.message?.task || null;
});

const timestamp = computed(() => {
  return props.message?.sent_at || resolvedTask.value?.created_at || null;
});

const taskTitle = computed(() => {
  if (resolvedTask.value?.title) return resolvedTask.value.title;
  if (resolvedTask.value?.summary) return resolvedTask.value.summary;
  if (props.message?.text) {
    const text = props.message.text;
    const prefixMatch = text.match(/^Linked task:\s*/i);
    return prefixMatch ? text.slice(prefixMatch[0].length) : text;
  }
  return "Untitled task";
});

const taskStatus = computed(() => {
  return resolvedTask.value?.status || "—";
});

const taskPriority = computed(() => {
  return resolvedTask.value?.priority || "—";
});

const taskDueDate = computed(() => {
  const date = resolvedTask.value?.due_date;
  if (!date) return "—";
  return formatTimestamp(date, "timestamp", formatTypes.centered);
});

const allUsers = computed(() => store.getters["users/users"] || []);

const assignees = computed(() => {
  const task = resolvedTask.value;
  const ownerIds = task?.assignees || task?.owner;
  if (!ownerIds) return [];
  const ids = Array.isArray(ownerIds) ? ownerIds : [ownerIds];
  return ids.map(id => {
    const user = allUsers.value.find(u => Number(u.id) === Number(id));
    return user || { id, name: `User ${id}` };
  });
});

const isExternalMember = computed(() => {
  const chat = props.chat || store.getters["chats/activeChat"];
  if (!chat || chat.type !== chatTypes.EXTERNAL) return false;
  const currentWsId = WorkspaceService.getCurrentWorkspaceId();
  return currentWsId && String(chat.workspace_id) !== String(currentWsId);
});

const navigateToTask = () => {
  // External group members from a different workspace cannot access tasks scoped to the group owner's workspace
  if (isExternalMember.value) return;
  const task = resolvedTask.value;
  if (!task?.id || !task?.project_id) return;
  router.push({
    name: "project-task-detail",
    params: {
      projectId: task.project_id,
      id: task.id,
    },
  });
};

const taskFields = computed(() => [
  { value: taskTitle.value, cols: 4, type: "text" },
  { value: taskStatus.value, cols: 2, type: "text" },
  { value: assignees.value, cols: 2, type: "assignees" },
  { value: taskPriority.value, cols: 2, type: "text" },
  { value: taskDueDate.value, cols: 2, type: "text" },
]);

const computedProfileIcon = computed(() => ({
  id: "replied",
  icon: imageNames.SUPER_AGI_LOGO_ROUND,
  size: 28,
}));

const setFieldRef = (el, index) => {
  try {
    if (el) {
      fieldRefs.value[index] = el;
      nextTick(() => {
        checkTruncation(index);
      });
    }
  } catch (error) {
    console.error("An error occurred while setting field ref: ", error);
  }
};

const checkTruncation = (index) => {
  try {
    const element = fieldRefs.value[index];
    if (element) {
      isTruncated.value[index] = element.scrollWidth > element.clientWidth;
    }
  } catch (error) {
    console.error("An error occurred while checking truncation: ", error);
  }
};

onMounted(() => {
  nextTick(() => {
    fieldRefs.value.forEach((_, index) => {
      checkTruncation(index);
    });
  });
});
</script>

<style scoped>
.task_message_container {
  background: var(--task-message-container-bg);
}
</style>
